import { Box, Button, Paper, TextField, Typography } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

import * as R from 'ramda'
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import FormMultiAutoCompleteSelector from '../../components/form/MultiAutoCompleteSelector'
import FormMultiFilesDropZone from '../../components/form/MultiFilesDropZone'
import FormSingleAutoCompleteSelector from '../../components/form/SingleAutoCompleteSelector'
import FormSingleFileDropZone from '../../components/form/SingleFileDropZone'
import FormSingleSelector from '../../components/form/SingleSelector'
import UserSearchAutoComplete from '../../components/form/UserSearchAutoComplete'
import { PROFILE_CATEGORY } from '../../constants/enums/company.enum.constant'
import { COUNTRY_LIST } from '../../constants/enums/country.enum.constant'
import { EMAIL_REGEX_PATTERN } from '../../constants/regexes/common.regex.constant'
import { CompanyDetailInterface } from '../../interfaces/company'

const StyledPaperContainer = styled(Paper)`
  padding: 10px;
  margin: 20px 10px;
`

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
`

const StyledLabelContainer = styled.div`
  width: 100%;
  margin-bottom: 15px;
`

const StyledFormGroupContainer = styled.div`
  margin: 20px 20% 20px 20%;
`

const StyledFormFieldContainer = styled.div`
  margin: 15px 0px 15px 0px;
  padding: 0px;
`

const StyledErrorMessageContainer = styled.div`
  height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
`

const StyledFormSubTitle = styled.div`
  margin-left: 15px;
`

// TODO: refactor to perform scroll inside the form
const StyledFormContainer = styled.div`
  padding: 0px 10px;
  overflow-y: auto;
  max-height: 70vh;
`

const StyledVerifiedStatusContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 80px;
`

const FormLabel = ({ name }: { name: string }) => (
  <StyledLabelContainer>
    <StyledLabel>{name}</StyledLabel>
  </StyledLabelContainer>
)

const FormErrorMessage = ({ message }: { message: string }) => (
  <Box component="div" display="line" fontSize={10} color="red">
    {message}
  </Box>
)

const CompanyFormMainBody = ({
  companyDetail,
  disableForm,
  newForm
}: {
  companyDetail: CompanyDetailInterface
  disableForm: boolean
  newForm: boolean
}) => {
  const { errors, control, setValue } = useFormContext()
  const textFieldInputProps = { style: { fontSize: 13 } }

  const [verified, setVerified] = useState<boolean>(
    R.pathOr(false, ['verified'], companyDetail)
  )

  const handleVerificationChange = () => {
    const newValue = !verified
    setVerified(newValue)
    setValue('verified', newValue)
  }

  return (
    <StyledFormContainer>
      <StyledPaperContainer elevation={3}>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Basic Information</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          {!newForm && (
            <StyledVerifiedStatusContainer>
              <StyledLabel style={{ marginRight: '15px' }}>
                Verification Status:
              </StyledLabel>
              <Controller
                name="verified"
                control={control}
                defaultValue={verified}
                as={
                  <>
                    <Typography
                      variant="subtitle1"
                      style={{ marginRight: '10px' }}
                    >
                      {verified ? 'Verified' : 'Not Verified'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      color={verified ? 'secondary' : 'primary'}
                      onClick={handleVerificationChange}
                    >
                      {verified ? 'Unverify' : 'Verify'}
                    </Button>
                  </>
                }
              />
            </StyledVerifiedStatusContainer>
          )}
          <StyledFormFieldContainer>
            <FormLabel name="Link User" />
            <UserSearchAutoComplete
              fieldName="linkedUserId"
              fieldValue={companyDetail?.linkedUser}
            />
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Profile Name" />
            <Controller
              name="name"
              control={control}
              defaultValue={R.pathOr('', ['name'], companyDetail)}
              rules={{
                required: {
                  value: true,
                  message: '* Your input is required'
                }
              }}
              render={({ onChange }) => (
                <TextField
                  onChange={onChange}
                  required
                  id="name-form-textfield-required"
                  label="Required"
                  variant="outlined"
                  defaultValue={R.pathOr('', ['name'], companyDetail)}
                  disabled={disableForm}
                  inputProps={textFieldInputProps}
                  fullWidth
                />
              )}
            />
            <StyledErrorMessageContainer>
              {errors.name && (
                <FormErrorMessage message={errors?.name?.message as string} />
              )}
            </StyledErrorMessageContainer>
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Profile Category" />
            <FormSingleSelector
              fieldName="profileCategory"
              selections={Object.values(PROFILE_CATEGORY)}
              defaultValue={PROFILE_CATEGORY.buyer}
              required
              disable={disableForm}
            />
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Country" />
            <FormSingleAutoCompleteSelector
              fieldName="country"
              fieldValue={R.prop('country', companyDetail)}
              selections={Object.values(COUNTRY_LIST)}
              defaultValue={COUNTRY_LIST['Hong Kong SAR']}
              required
              disable={disableForm}
            />
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Contact Email" />
            <Controller
              name="contactEmail"
              control={control}
              defaultValue={R.propOr('', 'contactEmail', companyDetail)}
              rules={{
                required: {
                  value: true,
                  message: '* Your input is required'
                },
                pattern: {
                  value: EMAIL_REGEX_PATTERN,
                  message: '* Invalid email address'
                }
              }}
              render={({ onChange }) => (
                <TextField
                  onChange={onChange}
                  required
                  id="contactEmail-form-textfield-required"
                  label="Required"
                  variant="outlined"
                  defaultValue={R.propOr('', 'contactEmail', companyDetail)}
                  disabled={disableForm}
                  inputProps={textFieldInputProps}
                  fullWidth
                />
              )}
            />
            <StyledErrorMessageContainer>
              {errors.contactEmail && (
                <FormErrorMessage
                  message={errors?.contactEmail?.message as string}
                />
              )}
            </StyledErrorMessageContainer>
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Description" />
            <Controller
              name="description"
              control={control}
              defaultValue={R.propOr('', 'description', companyDetail)}
              render={({ onChange }) => (
                <TextField
                  multiline
                  rows={10}
                  onChange={onChange}
                  id="outlined"
                  variant="outlined"
                  disabled={disableForm}
                  defaultValue={R.propOr('', 'description', companyDetail)}
                  inputProps={textFieldInputProps}
                  fullWidth
                />
              )}
            />
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Address" />
            <Controller
              name="address"
              control={control}
              defaultValue={R.propOr('', 'address', companyDetail)}
              render={({ onChange }) => (
                <TextField
                  multiline
                  rows={4}
                  onChange={onChange}
                  id="outlined"
                  variant="outlined"
                  disabled={disableForm}
                  defaultValue={R.propOr('', 'address', companyDetail)}
                  inputProps={textFieldInputProps}
                  fullWidth
                />
              )}
            />
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Established Year" />
            <Controller
              control={control}
              name="establishedYear"
              disabled={disableForm}
              defaultValue={null}
              minDate={new Date('1-1-1800')}
              maxDate={new Date()}
              autoOk
              as={
                <DatePicker
                  views={['year']}
                  value={R.pathOr(null, ['establishedYear'], companyDetail)}
                  onChange={(date: MaterialUiPickersDate) => {
                    setValue('establishedYear', date)
                    return { value: date }
                  }}
                  format="yyyy"
                />
              }
            />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
      <StyledPaperContainer elevation={3}>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Import from / Export to</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Imports From" />
            <FormMultiAutoCompleteSelector
              fieldName="importCountries"
              selections={Object.values(COUNTRY_LIST)}
              allOptionValue="All Countries"
              fieldValue={R.propOr([], 'importCountries', companyDetail)}
              disable={disableForm}
            />
          </StyledFormFieldContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Exports To" />
            <FormMultiAutoCompleteSelector
              fieldName="exportCountries"
              selections={Object.values(COUNTRY_LIST)}
              allOptionValue="All Countries"
              fieldValue={R.propOr([], 'exportCountries', companyDetail)}
              disable={disableForm}
            />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
      <StyledPaperContainer>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Images</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          <StyledFormFieldContainer>
            <FormLabel name="Logo Image" />
            <FormSingleFileDropZone
              fieldName="logoFile"
              disableForm={disableForm}
            />
            <FormLabel name="Banner Image" />
            <FormSingleFileDropZone
              fieldName="bannerFile"
              disableForm={disableForm}
            />
            <FormLabel name="Gallery Images" />
            <FormMultiFilesDropZone
              fieldName="galleryFiles"
              disableForm={disableForm}
            />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
      <StyledPaperContainer>
        <StyledFormSubTitle>
          <Typography variant="subtitle1">Other Files</Typography>
        </StyledFormSubTitle>
        <StyledFormGroupContainer>
          <StyledFormFieldContainer>
            <FormLabel name="BR" />
            <FormSingleFileDropZone
              fieldName="BRFile"
              disableForm={disableForm}
            />
            <FormLabel name="Certificates" />
            <FormMultiFilesDropZone
              fieldName="certificateFiles"
              disableForm={disableForm}
            />
          </StyledFormFieldContainer>
        </StyledFormGroupContainer>
      </StyledPaperContainer>
    </StyledFormContainer>
  )
}

export default CompanyFormMainBody
